import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SkeletonRows, Skeleton } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import type { Area } from 'react-easy-crop';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAll';
import { timeAgo } from '../lib/timeAgo';
import { VenuePhotoCropModal } from '../components/VenuePhotoCropModal';
import { useToast } from '../lib/useToast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { VenueEditModal, type VenueEditBase } from '../components/VenueEditModal';
import { ExportButton } from '../components/ExportButton';
import {
  cropImageToJpegBlob,
  resolveVenuePhotoCropUrl,
  uploadVenuePhotoBlob,
  venuePhotoStoragePath,
} from '../lib/cropImage';

type PhotoFilterKey = 'pending_photos' | 'approved_photos' | 'no_live_photo' | 'google_only' | 'single_photo_only';

const PHOTO_FILTER_OPTIONS: { key: PhotoFilterKey; label: string; title: string }[] = [
  {
    key: 'pending_photos',
    label: 'Pending photos only',
    title: 'Only venues with at least one photo awaiting approval',
  },
  {
    key: 'approved_photos',
    label: 'Has approved photos',
    title: 'Only venues with at least one approved (live) photo',
  },
  {
    key: 'no_live_photo',
    label: 'No live photo',
    title: 'No Google thumbnail and no approved photo',
  },
  {
    key: 'google_only',
    label: 'Using Google photo',
    title: 'No approved custom photo yet (Google may be live)',
  },
  {
    key: 'single_photo_only',
    label: 'Single photo only',
    title: 'Exactly one live image (Google or custom) — not a multi-photo carousel',
  },
];

/**
 * Additive metadata filters (all default OFF — the default list is unchanged).
 * Predicates run client-side against the per-venue record map (venueMetaById).
 */
type MetaFilterKey =
  | 'src_apple'
  | 'src_google'
  | 'src_foursquare'
  | 'src_owned'
  | 'reverse_searched'
  | 'missing_coords'
  | 'fsq_popular'
  | 'has_score';

const META_FILTER_OPTIONS: { key: MetaFilterKey; label: string; title: string }[] = [
  { key: 'src_apple', label: 'Source: Apple', title: 'platform_source = apple' },
  { key: 'src_google', label: 'Source: Google', title: 'platform_source = google' },
  { key: 'src_foursquare', label: 'Source: Foursquare', title: 'platform_source = foursquare' },
  { key: 'src_owned', label: 'Source: Owned', title: 'platform_source = owned' },
  { key: 'reverse_searched', label: 'Reverse-searched', title: 'reverse_searched = true' },
  { key: 'missing_coords', label: 'Missing coords', title: 'latitude or longitude is null' },
  { key: 'fsq_popular', label: 'FSQ popularity ≥ 0.72', title: 'fsq_popularity >= 0.72' },
  { key: 'has_score', label: 'Has Shado score', title: 'venue_shado_score is not null' },
];

type ListSortMode = 'ratings' | 'recent';

const ID_CHUNK = 120;

function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

async function fetchVenuesByIdsChunked(
  venueIds: string[],
): Promise<{ rows: VenueRow[]; support: VenueFieldSupport }> {
  if (venueIds.length === 0) return { rows: [], support: { description: false, websiteUrl: false } };
  let support: VenueFieldSupport = { description: false, websiteUrl: false };
  const rows: VenueRow[] = [];
  for (const chunk of chunkIds(venueIds, ID_CHUNK)) {
    const r = await fetchVenuesByIds(chunk);
    rows.push(...r.rows);
    support = {
      description: support.description || r.support.description,
      websiteUrl: support.websiteUrl || r.support.websiteUrl,
    };
  }
  return { rows, support };
}

async function mergeApprovedCountsChunked(venueIds: string[]): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  for (const chunk of chunkIds(venueIds, ID_CHUNK)) {
    const m = await fetchApprovedVenuePhotoCountsByVenue(chunk);
    m.forEach((n, id) => out.set(id, n));
  }
  return out;
}

type RatingEventRow = { venue_id: string | null; created_at: string | null };

async function fetchAllRatingEvents(): Promise<RatingEventRow[]> {
  try {
    return await fetchAllRows<RatingEventRow>((from, to) =>
      supabase.from('venue_logs').select('venue_id, created_at').range(from, to),
    );
  } catch {
    return await fetchAllRows<RatingEventRow>((from, to) =>
      supabase.from('logs').select('venue_id, created_at').is('retired_at', null).range(from, to),
    );
  }
}

function aggregateRatingsFromEvents(
  events: RatingEventRow[],
): { countByVenue: Map<string, number>; lastRatingAtByVenue: Map<string, string> } {
  const countByVenue = new Map<string, number>();
  const lastRatingAtByVenue = new Map<string, string>();
  for (const ev of events) {
    const vid = ev.venue_id;
    if (!vid) continue;
    countByVenue.set(vid, (countByVenue.get(vid) ?? 0) + 1);
    const t = ev.created_at;
    if (t && typeof t === 'string') {
      const prev = lastRatingAtByVenue.get(vid);
      if (!prev || t > prev) lastRatingAtByVenue.set(vid, t);
    }
  }
  return { countByVenue, lastRatingAtByVenue };
}

function venueMatchesActivePhotoFilters(
  v: VenueRow,
  approvedVenuePhotoCount: number,
  pendingCount: number,
  active: readonly PhotoFilterKey[],
): boolean {
  if (active.length === 0) return true;
  for (const f of active) {
    if (f === 'pending_photos' && pendingCount < 1) return false;
    if (f === 'approved_photos' && approvedVenuePhotoCount < 1) return false;
    if (f === 'no_live_photo' && livePhotoUrl(v)) return false;
    if (f === 'google_only' && !stillOnGoogle(v)) return false;
    if (f === 'single_photo_only' && !venueUsesExactlyOneLiveImage(v, approvedVenuePhotoCount)) return false;
  }
  return true;
}

async function fetchApprovedVenuePhotoCountsByVenue(venueIds: string[]): Promise<Map<string, number>> {
  const m = new Map<string, number>();
  if (venueIds.length === 0) return m;
  const rows = await fetchAllRows<{ venue_id: string | null }>((from, to) =>
    supabase.from('venue_photos').select('venue_id').eq('approved', true).in('venue_id', venueIds).range(from, to),
  );
  for (const r of rows) {
    const id = r.venue_id;
    if (!id) continue;
    m.set(id, (m.get(id) ?? 0) + 1);
  }
  return m;
}

async function fetchRatingCountsByVenue(venueIds: string[]): Promise<Map<string, number>> {
  const countMap = new Map<string, number>();
  if (venueIds.length === 0) return countMap;

  const bump = (rows: { venue_id: string | null }[]) => {
    for (const r of rows) {
      if (!r.venue_id) continue;
      countMap.set(r.venue_id, (countMap.get(r.venue_id) ?? 0) + 1);
    }
  };

  try {
    const rows = await fetchAllRows<{ venue_id: string | null }>((from, to) =>
      supabase.from('venue_logs').select('venue_id').in('venue_id', venueIds).range(from, to),
    );
    bump(rows);
    return countMap;
  } catch {
    countMap.clear();
  }

  const rows = await fetchAllRows<{ venue_id: string | null }>((from, to) =>
    supabase.from('logs').select('venue_id').in('venue_id', venueIds).is('retired_at', null).range(from, to),
  );
  bump(rows);
  return countMap;
}

type PendingPhotoRow = {
  id: string;
  venue_id: string;
  user_id: string | null;
  source: string | null;
  public_url: string | null;
  created_at: string;
};

/** Core columns always selected. Optional: `description`, `website_url` — see fetchVenuesByIds(). */
type VenueRow = {
  id: string;
  venue_name_user_input: string | null;
  city_country: string | null;
  fsq_address: string | null;
  description: string | null;
  website_url: string | null;
  google_photo_r2_url: string | null;
  approved_venue_photo_url: string | null;
  google_fetch_disabled?: boolean | null;
};

type VenueFieldSupport = { description: boolean; websiteUrl: boolean };

const VENUE_SELECT_CORE =
  'id, venue_name_user_input, city_country, fsq_address, google_photo_r2_url, approved_venue_photo_url';

// When these columns exist on `public.venues`, they are merged into select (see fetchVenuesByIds).
// description text null,
// website_url text null,

/** Only retry without optional columns when PostgREST reports missing description/website_url. */
function optionalVenueColumnsMissing(message: string): boolean {
  const m = message.toLowerCase();
  if (!m.includes('description') && !m.includes('website_url')) return false;
  return (
    m.includes('does not exist') ||
    m.includes('could not find') ||
    m.includes('42703') ||
    m.includes('schema cache')
  );
}

function escapeForIlike(term: string): string {
  return term.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

async function fetchVenuesByIds(venueIds: string[]): Promise<{ rows: VenueRow[]; support: VenueFieldSupport }> {
  if (venueIds.length === 0) return { rows: [], support: { description: false, websiteUrl: false } };

  const withOptional = `${VENUE_SELECT_CORE}, description, website_url`;
  const res = await supabase.from('venues').select(withOptional).in('id', venueIds);
  if (!res.error && res.data) {
    return {
      rows: (res.data ?? []) as unknown as VenueRow[],
      support: { description: true, websiteUrl: true },
    };
  }
  if (res.error && optionalVenueColumnsMissing(res.error.message)) {
    const fallback = await supabase.from('venues').select(VENUE_SELECT_CORE).in('id', venueIds);
    if (fallback.error) throw fallback.error;
    const rows = (fallback.data ?? []).map((r) => ({
      ...(r as unknown as Omit<VenueRow, 'description' | 'website_url'>),
      description: null,
      website_url: null,
    }));
    return { rows, support: { description: false, websiteUrl: false } };
  }
  if (res.error) throw res.error;
  return { rows: [], support: { description: false, websiteUrl: false } };
}

function pickString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
}

function venueName(v: VenueRow): string {
  return pickString(v.venue_name_user_input) ?? 'Untitled venue';
}

function venueCity(v: VenueRow): string {
  return pickString(v.city_country) ?? '—';
}

function livePhotoUrl(v: VenueRow): string | null {
  return pickString(v.approved_venue_photo_url) ?? pickString(v.google_photo_r2_url);
}

function stillOnGoogle(v: VenueRow): boolean {
  return !pickString(v.approved_venue_photo_url);
}

/** One visible image: either a single approved venue_photos row, or Google / venue URL only (no carousel). */
function venueUsesExactlyOneLiveImage(v: VenueRow, approvedVenuePhotoCount: number): boolean {
  if (approvedVenuePhotoCount >= 2) return false;
  if (approvedVenuePhotoCount === 1) return true;
  return Boolean(livePhotoUrl(v));
}

function sourceLabel(src: string | null): string {
  const s = (src ?? '').toLowerCase();
  if (s === 'venue_upload') return 'Venue Upload';
  if (s === 'rating_photo') return 'From Rating';
  return pickString(src) ?? '—';
}

type ApprovedPhotoRow = {
  id: string;
  venue_id: string;
  user_id: string | null;
  public_url: string | null;
  source: string | null;
  created_at: string;
  display_order: number | null;
};

/** Every venue_photos row ever written for a venue, regardless of status — nothing is ever
 * hard-deleted by this dashboard (approve/reject/delete all just flip flags), so this is a
 * durable submission history. */
type AnyPhotoRow = {
  id: string;
  venue_id: string;
  user_id: string | null;
  public_url: string | null;
  source: string | null;
  approved: boolean;
  approved_at: string | null;
  rejected: boolean;
  created_at: string;
};

type ProfileLite = { id: string; username: string | null; avatar_url: string | null };

/** Defensive: returns whatever rows succeed; a missing column/grant never breaks the page. */
async function fetchProfilesByIds(userIds: string[]): Promise<Map<string, ProfileLite>> {
  const out = new Map<string, ProfileLite>();
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  if (ids.length === 0) return out;
  for (const chunk of chunkIds(ids, ID_CHUNK)) {
    try {
      const { data, error } = await supabase.from('profiles').select('id, username, avatar_url').in('id', chunk);
      if (error) throw error;
      for (const r of (data ?? []) as ProfileLite[]) {
        if (r?.id) out.set(r.id, r);
      }
    } catch {
      /* missing grant/column must never break the page */
    }
  }
  return out;
}

function photoStatusLabel(p: AnyPhotoRow): { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } {
  if (p.approved) return { label: 'Live', tone: 'success' };
  if (p.rejected && p.approved_at) return { label: 'Removed', tone: 'danger' };
  if (p.rejected) return { label: 'Rejected', tone: 'danger' };
  return { label: 'Pending', tone: 'warning' };
}

/**
 * Full read-only venue record (detail "Record" subsection + metadata filters + export).
 * Fetched defensively: a missing column / query error never breaks the page (empty map).
 */
type VenueRecord = {
  id: string;
  venue_shado_score: number | null;
  venue_shado_unique_users: number | null;
  venue_shado_rating_count: number | null;
  fsq_popularity: number | null;
  platform_source: string | null;
  latitude: number | null;
  longitude: number | null;
  city_id: string | null;
  fsq_place_id: string | null;
  google_place_id: string | null;
  apple_place_id: string | null;
  reverse_searched: boolean | null;
  created_at: string | null;
};

const VENUE_RECORD_SELECT =
  'id, venue_shado_score, venue_shado_unique_users, venue_shado_rating_count, fsq_popularity, platform_source, latitude, longitude, city_id, fsq_place_id, google_place_id, apple_place_id, reverse_searched, created_at';

/** Defensive: returns whatever rows succeed; any chunk error is swallowed (graceful fallback). */
async function fetchVenueRecordsByIds(venueIds: string[]): Promise<Map<string, VenueRecord>> {
  const out = new Map<string, VenueRecord>();
  if (venueIds.length === 0) return out;
  for (const chunk of chunkIds(venueIds, ID_CHUNK)) {
    try {
      const { data, error } = await supabase.from('venues').select(VENUE_RECORD_SELECT).in('id', chunk);
      if (error) throw error;
      for (const r of (data ?? []) as unknown as VenueRecord[]) {
        if (r && r.id) out.set(r.id, r);
      }
    } catch {
      /* missing column or query error must never break the page */
    }
  }
  return out;
}

function venueMatchesActiveMetaFilters(
  rec: VenueRecord | undefined,
  active: readonly MetaFilterKey[],
): boolean {
  if (active.length === 0) return true;
  const src = (rec?.platform_source ?? '').toLowerCase();
  for (const f of active) {
    if (f === 'src_apple' && src !== 'apple') return false;
    if (f === 'src_google' && src !== 'google') return false;
    if (f === 'src_foursquare' && src !== 'foursquare' && src !== 'fsq') return false;
    if (f === 'src_owned' && src !== 'owned') return false;
    if (f === 'reverse_searched' && rec?.reverse_searched !== true) return false;
    if (f === 'missing_coords' && !(rec == null || rec.latitude == null || rec.longitude == null)) return false;
    if (f === 'fsq_popular' && !((rec?.fsq_popularity ?? -1) >= 0.72)) return false;
    if (f === 'has_score' && (rec == null || rec.venue_shado_score == null)) return false;
  }
  return true;
}

/**
 * ⚠️ ERROR BEACON — AND WHY IT IS HERE AND NOT ONLY ON THE OTHER SCREEN (2026-09-22).
 *
 * Evan has reported photo approve/reject "not working" three times. Three rounds of
 * instrumentation were added to `src/pages/PhotoApprovalScreen.tsx`, and every round learned
 * NOTHING — the `admin_client_errors` table stayed EMPTY. That screen beacons on MOUNT, so an
 * empty table is proof it was never opened: **Evan is on `/venues` (this screen), not
 * `/photo-approval`.** Both nav items carry the same pending-photos badge and both approve venue
 * photos, which is how three rounds went to the wrong door.
 *
 * Fixed here rather than by deleting a screen, because which of the two survives is Evan's call.
 */
function beacon(page: string, message: string, detail?: Record<string, unknown>): void {
  void supabase.from('admin_client_errors').insert({ page, message, detail: detail ?? null });
}

export function VenueReviewScreen({ user }: { user: User }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhotoRow[]>([]);
  const [venues, setVenues] = useState<VenueRow[]>([]);
  const [ratingCountByVenue, setRatingCountByVenue] = useState<Map<string, number>>(new Map());
  const [lastRatingAtByVenue, setLastRatingAtByVenue] = useState<Map<string, string>>(new Map());
  const [approvedVenuePhotoCountByVenue, setApprovedVenuePhotoCountByVenue] = useState<Map<string, number>>(
    new Map(),
  );
  const [activePhotoFilters, setActivePhotoFilters] = useState<PhotoFilterKey[]>([]);
  const [activeMetaFilters, setActiveMetaFilters] = useState<MetaFilterKey[]>([]);
  const [venueMetaById, setVenueMetaById] = useState<Map<string, VenueRecord>>(new Map());
  const [listSortMode, setListSortMode] = useState<ListSortMode>('ratings');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [venueFieldSupport, setVenueFieldSupport] = useState<VenueFieldSupport>({
    description: false,
    websiteUrl: false,
  });
  const [venueEditDraft, setVenueEditDraft] = useState({
    venue_name_user_input: '',
    fsq_address: '',
    description: '',
    website_url: '',
  });
  const [venueEditBaseline, setVenueEditBaseline] = useState({
    venue_name_user_input: '',
    fsq_address: '',
    description: '',
    website_url: '',
  });
  const [venueSaveBusy, setVenueSaveBusy] = useState(false);
  /** Open the shared lat/lng/coords_approximate/fsq edit modal for a venue id. */
  const [editVenueId, setEditVenueId] = useState<string | null>(null);
  const [editVenueBase, setEditVenueBase] = useState<VenueEditBase | null>(null);
  const [cropPhoto, setCropPhoto] = useState<PendingPhotoRow | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropBusy, setCropBusy] = useState(false);
  /** Approved photos for the currently-selected venue (the gallery + per-photo delete UI). */
  const [selectedApprovedPhotos, setSelectedApprovedPhotos] = useState<ApprovedPhotoRow[]>([]);
  const [approvedPhotosLoading, setApprovedPhotosLoading] = useState(false);
  const [deletePhotoTarget, setDeletePhotoTarget] = useState<ApprovedPhotoRow | null>(null);
  const [deletePhotoBusy, setDeletePhotoBusy] = useState(false);
  const [reorderBusy, setReorderBusy] = useState(false);
  /** Full submission history for the selected venue (every status, newest first). */
  const [allSubmissions, setAllSubmissions] = useState<AnyPhotoRow[]>([]);
  const [allSubmissionsLoading, setAllSubmissionsLoading] = useState(false);
  /** username/avatar lookup for photo submitters — filled on demand, never re-fetched once known. */
  const [profileById, setProfileById] = useState<Map<string, ProfileLite>>(new Map());
  /** Resizable split between the venue table and the detail panel (width persisted). */
  const splitRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState<number>(() => {
    // v2 key: reset everyone to the new thin default (old saved widths were wide).
    const saved = Number(window.localStorage.getItem('admin:venueListWidth:v2'));
    return saved && saved >= 240 ? saved : 340;
  });
  const [resizing, setResizing] = useState(false);

  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [debouncedVenueSearch, setDebouncedVenueSearch] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState<
    { id: string; venue_name_user_input: string | null; city_country: string | null }[]
  >([]);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);

  /** Proves which of the two photo screens is actually being used. See the beacon note above. */
  useEffect(() => {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    beacon('venues/mount', `navigation=${nav?.type ?? 'unknown'}`, { href: window.location.pathname });
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedVenueSearch(venueSearchQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [venueSearchQuery]);

  useEffect(() => {
    if (!cropPhoto?.public_url) {
      setCropImageUrl(null);
      return;
    }
    let cancelled = false;
    void resolveVenuePhotoCropUrl(cropPhoto.public_url).then((url) => {
      if (!cancelled) setCropImageUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [cropPhoto?.public_url]);

  const loadApprovedPhotos = useCallback(async (venueId: string) => {
    setApprovedPhotosLoading(true);
    try {
      const { data, error } = await supabase
        .from('venue_photos')
        .select('id, venue_id, user_id, public_url, source, created_at, display_order')
        .eq('venue_id', venueId)
        .eq('approved', true)
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('approved_at', { ascending: false });
      if (error) throw error;
      setSelectedApprovedPhotos((data ?? []) as ApprovedPhotoRow[]);
    } catch {
      setSelectedApprovedPhotos([]);
    } finally {
      setApprovedPhotosLoading(false);
    }
  }, []);

  /** Every photo ever submitted for this venue — approved, pending, rejected, or later
   * removed — so nothing an operator needs to audit is ever truly gone from view. */
  const loadAllSubmissions = useCallback(async (venueId: string) => {
    setAllSubmissionsLoading(true);
    try {
      const { data, error } = await supabase
        .from('venue_photos')
        .select('id, venue_id, user_id, public_url, source, approved, approved_at, rejected, created_at')
        .eq('venue_id', venueId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAllSubmissions((data ?? []) as AnyPhotoRow[]);
    } catch {
      setAllSubmissions([]);
    } finally {
      setAllSubmissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelectedApprovedPhotos([]);
      setAllSubmissions([]);
      return;
    }
    void loadApprovedPhotos(selectedId);
    void loadAllSubmissions(selectedId);
  }, [selectedId, loadApprovedPhotos, loadAllSubmissions]);

  // Resolve submitter usernames/avatars for everyone visible on this screen (pending queue +
  // the selected venue's approved photos + its full submission history). Additive-only cache.
  useEffect(() => {
    const ids = new Set<string>();
    for (const p of pendingPhotos) if (p.user_id) ids.add(p.user_id);
    for (const p of selectedApprovedPhotos) if (p.user_id) ids.add(p.user_id);
    for (const p of allSubmissions) if (p.user_id) ids.add(p.user_id);
    const missing = Array.from(ids).filter((id) => !profileById.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    void (async () => {
      const m = await fetchProfilesByIds(missing);
      if (cancelled || m.size === 0) return;
      setProfileById((prev) => {
        const next = new Map(prev);
        m.forEach((v, k) => next.set(k, v));
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingPhotos, selectedApprovedPhotos, allSubmissions, profileById]);

  function submitterLabel(userId: string | null): string {
    if (!userId) return 'Unknown';
    const p = profileById.get(userId);
    if (p?.username) return `@${p.username}`;
    return `${userId.slice(0, 8)}…`;
  }

  // Ensure the full record is available for the selected venue (e.g. opened via search).
  useEffect(() => {
    if (!selectedId || venueMetaById.has(selectedId)) return;
    let cancelled = false;
    void (async () => {
      const m = await fetchVenueRecordsByIds([selectedId]);
      if (cancelled || m.size === 0) return;
      setVenueMetaById((prev) => {
        const next = new Map(prev);
        m.forEach((v, k) => next.set(k, v));
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, venueMetaById]);

  useEffect(() => {
    window.localStorage.setItem('admin:venueListWidth:v2', String(Math.round(leftWidth)));
  }, [leftWidth]);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e: MouseEvent) => {
      const rect = splitRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = Math.min(Math.max(e.clientX - rect.left, 240), rect.width - 420);
      setLeftWidth(next);
    };
    const onUp = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: pendingPhotosData, error: pe }, ratingEvents] = await Promise.all([
        supabase
          .from('venue_photos')
          .select('id, venue_id, user_id, source, public_url, created_at')
          .eq('approved', false)
          .eq('rejected', false)
          .order('source', { ascending: true })
          .order('created_at', { ascending: false }),
        fetchAllRatingEvents(),
      ]);
      if (pe) throw pe;

      const list = (pendingPhotosData ?? []) as PendingPhotoRow[];
      setPendingPhotos(list);

      const { countByVenue, lastRatingAtByVenue } = aggregateRatingsFromEvents(ratingEvents);
      const ratedIds = Array.from(countByVenue.keys()).filter((id) => (countByVenue.get(id) ?? 0) > 0);

      setRatingCountByVenue(new Map(countByVenue));
      setLastRatingAtByVenue(new Map(lastRatingAtByVenue));

      if (ratedIds.length === 0) {
        setVenues([]);
        setApprovedVenuePhotoCountByVenue(new Map());
        setVenueFieldSupport({ description: false, websiteUrl: false });
        setVenueMetaById(new Map());
        return;
      }

      const [{ rows: venueRows, support }, approvedMerged, recordsMerged] = await Promise.all([
        fetchVenuesByIdsChunked(ratedIds),
        mergeApprovedCountsChunked(ratedIds),
        fetchVenueRecordsByIds(ratedIds),
      ]);
      setVenues(venueRows);
      setVenueFieldSupport(support);
      setApprovedVenuePhotoCountByVenue(approvedMerged);
      setVenueMetaById(recordsMerged);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setPendingPhotos([]);
      setVenues([]);
      setRatingCountByVenue(new Map());
      setLastRatingAtByVenue(new Map());
      setApprovedVenuePhotoCountByVenue(new Map());
      setVenueFieldSupport({ description: false, websiteUrl: false });
      setVenueMetaById(new Map());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // On-demand fetch of the venue's editable fields when the modal opens.
  // Avoids enlarging the main VenueRow type / SELECT statement.
  useEffect(() => {
    if (!editVenueId) {
      setEditVenueBase(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await supabase
        .from('venues')
        .select('id, venue_name_user_input, latitude, longitude, coords_approximate, fsq_place_id, apple_place_id, platform_source')
        .eq('id', editVenueId)
        .maybeSingle();
      if (cancelled) return;
      if (e || !data) {
        setEditVenueBase(null);
        return;
      }
      setEditVenueBase(data as VenueEditBase);
    })();
    return () => {
      cancelled = true;
    };
  }, [editVenueId]);

  useEffect(() => {
    const raw = debouncedVenueSearch.replace(/,/g, ' ').trim();
    if (!raw) {
      setVenueSearchResults([]);
      setVenueSearchLoading(false);
      return;
    }
    let cancelled = false;
    setVenueSearchLoading(true);
    const pattern = `%${escapeForIlike(raw)}%`;
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('venues')
          .select('id, venue_name_user_input, city_country')
          .or(
            `venue_name_user_input.ilike.${pattern},city_country.ilike.${pattern},fsq_address.ilike.${pattern}`,
          )
          .order('venue_name_user_input', { ascending: true })
          .limit(50);
        if (cancelled) return;
        if (error) throw error;
        setVenueSearchResults(
          (data ?? []) as { id: string; venue_name_user_input: string | null; city_country: string | null }[],
        );
      } catch {
        if (!cancelled) setVenueSearchResults([]);
      } finally {
        if (!cancelled) setVenueSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedVenueSearch]);

  const { showError, showSuccess, toastNode } = useToast();

  const openVenueFromSearch = useCallback(async (venueId: string) => {
    try {
      const { rows, support } = await fetchVenuesByIds([venueId]);
      const row = rows[0];
      if (!row) return;
      setVenueFieldSupport((prev) => ({
        description: prev.description || support.description,
        websiteUrl: prev.websiteUrl || support.websiteUrl,
      }));
      setVenues((prev) => {
        const idx = prev.findIndex((v) => v.id === row.id);
        if (idx === -1) return [...prev, row];
        const next = [...prev];
        next[idx] = row;
        return next;
      });
      const [counts, approvedCounts] = await Promise.all([
        fetchRatingCountsByVenue([venueId]),
        fetchApprovedVenuePhotoCountsByVenue([venueId]),
      ]);
      setRatingCountByVenue((prev) => {
        const next = new Map(prev);
        next.set(venueId, counts.get(venueId) ?? 0);
        return next;
      });
      setApprovedVenuePhotoCountByVenue((prev) => {
        const next = new Map(prev);
        next.set(venueId, approvedCounts.get(venueId) ?? 0);
        return next;
      });
      setSelectedId(venueId);
      setVenueSearchQuery('');
      setDebouncedVenueSearch('');
      setVenueSearchResults([]);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not load venue');
    }
  }, [showError]);

  const pendingByVenue = useMemo(() => {
    const m = new Map<string, PendingPhotoRow[]>();
    for (const p of pendingPhotos) {
      const vid = p.venue_id;
      if (!vid) continue;
      if (!m.has(vid)) m.set(vid, []);
      m.get(vid)!.push(p);
    }
    return m;
  }, [pendingPhotos]);

  const togglePhotoFilter = useCallback((key: PhotoFilterKey) => {
    setActivePhotoFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const toggleMetaFilter = useCallback((key: MetaFilterKey) => {
    setActiveMetaFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const filteredSortedVenues = useMemo(() => {
    let list = venues.filter(
      (v) =>
        venueMatchesActivePhotoFilters(
          v,
          approvedVenuePhotoCountByVenue.get(v.id) ?? 0,
          pendingByVenue.get(v.id)?.length ?? 0,
          activePhotoFilters,
        ) && venueMatchesActiveMetaFilters(venueMetaById.get(v.id), activeMetaFilters),
    );
    list = [...list].sort((a, b) => {
      if (listSortMode === 'recent') {
        const ta = lastRatingAtByVenue.get(a.id) ?? '';
        const tb = lastRatingAtByVenue.get(b.id) ?? '';
        if (tb !== ta) return tb.localeCompare(ta);
      }
      const ra = ratingCountByVenue.get(a.id) ?? 0;
      const rb = ratingCountByVenue.get(b.id) ?? 0;
      if (rb !== ra) return rb - ra;
      return venueName(a).localeCompare(venueName(b));
    });
    return list;
  }, [
    venues,
    pendingByVenue,
    activePhotoFilters,
    activeMetaFilters,
    venueMetaById,
    ratingCountByVenue,
    approvedVenuePhotoCountByVenue,
    lastRatingAtByVenue,
    listSortMode,
  ]);

  const venueById = useMemo(() => new Map(venues.map((v) => [v.id, v])), [venues]);

  const selected = selectedId ? venueById.get(selectedId) ?? null : null;
  const selectedPending = selectedId ? pendingByVenue.get(selectedId) ?? [] : [];

  useEffect(() => {
    if (!selected) return;
    const next = {
      venue_name_user_input: selected.venue_name_user_input ?? '',
      fsq_address: selected.fsq_address ?? '',
      description: selected.description ?? '',
      website_url: selected.website_url ?? '',
    };
    setVenueEditDraft(next);
    setVenueEditBaseline({ ...next });
  }, [selected]);

  const venueEditDirty = useMemo(() => {
    return (
      venueEditDraft.venue_name_user_input !== venueEditBaseline.venue_name_user_input ||
      venueEditDraft.fsq_address !== venueEditBaseline.fsq_address ||
      venueEditDraft.description !== venueEditBaseline.description ||
      venueEditDraft.website_url !== venueEditBaseline.website_url
    );
  }, [venueEditDraft, venueEditBaseline]);

  async function deleteVenue() {
    if (!selected) return;
    const id = selected.id;
    setDeleteBusy(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_delete_venue', { p_venue_id: id });
      if (rpcErr) {
        const m = rpcErr.message.toLowerCase();
        if (m.includes('admin_delete_venue') || m.includes('42883') || m.includes('could not find the function')) {
          throw new Error('Delete is not enabled yet — run `supabase db push` to apply migration 20260762000000.');
        }
        throw rpcErr;
      }
      if (data && (data as { ok?: boolean }).ok === false) {
        throw new Error((data as { error?: string }).error ?? 'Delete failed');
      }
      setVenues((prev) => prev.filter((v) => v.id !== id));
      setSelectedId(null);
      setConfirmDeleteOpen(false);
      showSuccess('Venue deleted');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  }

  async function saveVenueDetails() {
    if (!selected || !venueEditDirty) return;
    setVenueSaveBusy(true);
    try {
      // Name must go through SECURITY DEFINER RPC — direct update is a silent no-op due to
      // RLS policy venues_update_logger (requires owning a log at the venue).
      const newName = venueEditDraft.venue_name_user_input.trim() || null;
      const baselineName = venueEditBaseline.venue_name_user_input.trim() || null;
      const nameChanged = newName !== baselineName;
      if (nameChanged && newName) {
        const { error: nameErr } = await supabase.rpc('admin_set_venue_name', {
          p_venue_id: selected.id,
          p_name: newName,
        });
        if (nameErr) throw nameErr;
      }

      // All other editable fields go through the direct update (address, description, website).
      const payload: Record<string, string | null> = {
        fsq_address: venueEditDraft.fsq_address.trim() || null,
      };
      if (venueFieldSupport.description) {
        payload.description = venueEditDraft.description.trim() || null;
      }
      if (venueFieldSupport.websiteUrl) {
        payload.website_url = venueEditDraft.website_url.trim() || null;
      }
      const { error: upErr } = await supabase.from('venues').update(payload).eq('id', selected.id);
      if (upErr) throw upErr;

      setVenues((prev) => {
        const idx = prev.findIndex((v) => v.id === selected.id);
        const patch = {
          ...selected,
          venue_name_user_input: nameChanged && newName ? newName : selected.venue_name_user_input,
          fsq_address: payload.fsq_address,
          ...(venueFieldSupport.description ? { description: payload.description ?? null } : {}),
          ...(venueFieldSupport.websiteUrl ? { website_url: payload.website_url ?? null } : {}),
        } as VenueRow;
        if (idx === -1) return [...prev, patch];
        return prev.map((v) => (v.id === selected.id ? patch : v));
      });
      setVenueEditBaseline({ ...venueEditDraft });
      showSuccess('Venue details saved');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setVenueSaveBusy(false);
    }
  }

  async function approvePhoto(photo: PendingPhotoRow, approvedPublicUrl?: string) {
    const url = pickString(approvedPublicUrl ?? photo.public_url);
    if (!url || !photo.venue_id) return;
    setActionId(photo.id);
    try {
      const { data: venueRow, error: readErr } = await supabase
        .from('venues')
        .select('id, google_photo_r2_url, approved_venue_photo_url')
        .eq('id', photo.venue_id)
        .maybeSingle();
      if (readErr) throw readErr;
      const oldGoogleUrl = venueRow?.google_photo_r2_url ?? null;
      // A primary is already locked in (a prior approval, or a manual "Make primary") —
      // this approval must never displace it. Only the FIRST-ever approval (or explicit
      // "Make primary") sets venues.approved_venue_photo_url.
      const hasExistingPrimary = pickString(venueRow?.approved_venue_photo_url ?? null) != null;

      // Normalize the venue's current approved-photo order into explicit 0..n-1 values
      // (legacy rows may still have display_order = null), then append this one at n —
      // i.e. the true end of the carousel, never disturbing what's already ordered.
      const { data: existingApproved, error: exErr } = await supabase
        .from('venue_photos')
        .select('id, display_order')
        .eq('venue_id', photo.venue_id)
        .eq('approved', true)
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('approved_at', { ascending: false });
      if (exErr) throw exErr;
      const existingOrdered = existingApproved ?? [];
      const renumbers = existingOrdered
        .map((p, idx) => ({ id: p.id as string, order: idx, changed: p.display_order !== idx }))
        .filter((p) => p.changed);
      const nextOrder = existingOrdered.length;

      const now = new Date().toISOString();
      const results = await Promise.all([
        // `.select('id')` for the same reason as in `rejectPhoto` — a 0-row UPDATE returns no error.
        supabase
          .from('venue_photos')
          .update({ approved: true, approved_by: user.id, approved_at: now, public_url: url, display_order: nextOrder })
          .eq('id', photo.id)
          .select('id'),
        ...renumbers.map((r) => supabase.from('venue_photos').update({ display_order: r.order }).eq('id', r.id)),
      ]);
      const firstUpdateErr = results.find((r) => r.error)?.error;
      if (firstUpdateErr) throw firstUpdateErr;
      const approvedRows = (results[0] as { data?: unknown[] | null }).data;
      if (!approvedRows || approvedRows.length === 0) {
        throw new Error('Approve changed 0 rows — the write was silently dropped.');
      }

      if (!hasExistingPrimary) {
        // RLS blocks a direct admin UPDATE on venues (venues_update_logger) — route through the
        // SECURITY DEFINER RPC, which also clears the Google cache + disables future fetch.
        const { error: e2 } = await supabase.rpc('admin_set_approved_venue_photo', {
          p_venue_id: photo.venue_id,
          p_url: url,
        });
        if (e2) throw e2;
      }

      if (!hasExistingPrimary && oldGoogleUrl) {
        try {
          const marker = '/venue-photos/';
          const idx = oldGoogleUrl.indexOf(marker);
          if (idx >= 0) {
            const path = oldGoogleUrl.slice(idx + marker.length);
            await supabase.storage.from('venue-photos').remove([path]);
          }
        } catch {
          /* non-blocking */
        }
      }

      if (approvedPublicUrl && photo.public_url) {
        try {
          const oldPath = venuePhotoStoragePath(photo.public_url);
          const newPath = venuePhotoStoragePath(url);
          if (oldPath && oldPath !== newPath) {
            await supabase.storage.from('venue-photos').remove([oldPath]);
          }
        } catch {
          /* non-blocking */
        }
      }

      setPendingPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setVenues((prev) =>
        prev.map((v) =>
          v.id === photo.venue_id
            ? {
                ...v,
                ...(hasExistingPrimary
                  ? {}
                  : { approved_venue_photo_url: url, google_photo_r2_url: null, google_fetch_disabled: true }),
              }
            : v,
        ),
      );
      setApprovedVenuePhotoCountByVenue((prev) => {
        const next = new Map(prev);
        const vid = photo.venue_id;
        next.set(vid, (next.get(vid) ?? 0) + 1);
        return next;
      });
      showSuccess(hasExistingPrimary ? 'Photo approved — added to the end of the gallery' : 'Photo approved and set as primary');
      if (selectedId === photo.venue_id) {
        void loadApprovedPhotos(photo.venue_id);
        void loadAllSubmissions(photo.venue_id);
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setActionId(null);
    }
  }

  async function confirmCropAndApprove(pixelCrop: Area) {
    if (!cropPhoto?.public_url || !cropPhoto.venue_id) return;
    setCropBusy(true);
    try {
      const sourceUrl = await resolveVenuePhotoCropUrl(cropPhoto.public_url);
      const blob = await cropImageToJpegBlob(sourceUrl, pixelCrop);
      const uploadPath = `admin-approved/${cropPhoto.venue_id}/${cropPhoto.id}.jpg`;
      const { publicUrl } = await uploadVenuePhotoBlob(uploadPath, blob);
      await approvePhoto(cropPhoto, publicUrl);
      setCropPhoto(null);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Crop or approve failed');
    } finally {
      setCropBusy(false);
    }
  }

  async function rejectPhoto(photo: PendingPhotoRow) {
    setActionId(photo.id);
    try {
      /**
       * ⚠️ `.select('id')` IS THE FIX, NOT DECORATION. A Supabase UPDATE that matches ZERO rows
       * returns **no error** — so the old code threw nothing, dropped the row from the list and
       * said "Photo rejected" while the database was untouched. The next refetch put the photo
       * back, which is exactly what "it just refreshes and nothing happens" looks like.
       */
      const { data: rejected, error } = await supabase
        .from('venue_photos')
        .update({ rejected: true })
        .eq('id', photo.id)
        .select('id');
      if (error) throw error;
      if (!rejected || rejected.length === 0) {
        throw new Error('Reject changed 0 rows — the write was silently dropped.');
      }
      setPendingPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      showSuccess('Photo rejected');
      if (selectedId === photo.venue_id) void loadAllSubmissions(photo.venue_id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Reject failed';
      beacon('venues/reject', msg, { photoId: photo.id, venueId: photo.venue_id });
      showError(msg);
    } finally {
      setActionId(null);
    }
  }

  async function deleteApprovedPhoto(photo: ApprovedPhotoRow) {
    if (!photo.venue_id) return;
    setDeletePhotoBusy(true);
    try {
      /**
       * RLS lets admins UPDATE venue_photos but not DELETE — so this is a soft
       * delete (un-approve + mark rejected) paired with physically removing the
       * stored image. Everything that reads venue photos gates on `approved = true`,
       * so the photo disappears app-wide.
       */
      const { error: upErr } = await supabase
        .from('venue_photos')
        .update({ approved: false, rejected: true })
        .eq('id', photo.id);
      if (upErr) throw upErr;

      if (photo.public_url) {
        try {
          const path = venuePhotoStoragePath(photo.public_url);
          if (path) await supabase.storage.from('venue-photos').remove([path]);
        } catch {
          /* non-blocking: row is already un-approved */
        }
      }

      /** If the deleted photo was the live hero, repoint to another approved photo (or clear it),
       * and lock that fallback into position #1 — same invariant as "Make primary". */
      let remaining = selectedApprovedPhotos.filter((p) => p.id !== photo.id);
      const venue = venueById.get(photo.venue_id) ?? null;
      if (venue && pickString(venue.approved_venue_photo_url) === pickString(photo.public_url)) {
        const fallback = remaining.find((p) => pickString(p.public_url)) ?? null;
        const { error: vErr } = await supabase.rpc('admin_set_approved_venue_photo', {
          p_venue_id: photo.venue_id,
          p_url: fallback ? pickString(fallback.public_url) : null,
        });
        if (vErr) throw vErr;
        setVenues((prev) =>
          prev.map((v) =>
            v.id === photo.venue_id
              ? { ...v, approved_venue_photo_url: fallback ? pickString(fallback.public_url) : null }
              : v,
          ),
        );
        if (fallback) {
          const reordered = [fallback, ...remaining.filter((p) => p.id !== fallback.id)];
          const results = await Promise.all(
            reordered.map((p, idx) => supabase.from('venue_photos').update({ display_order: idx }).eq('id', p.id)),
          );
          const firstErr = results.find((r) => r.error)?.error;
          if (firstErr) throw firstErr;
          remaining = reordered.map((p, idx) => ({ ...p, display_order: idx }));
        }
      }

      setSelectedApprovedPhotos(remaining);
      setApprovedVenuePhotoCountByVenue((prev) => {
        const next = new Map(prev);
        next.set(photo.venue_id, Math.max(0, (next.get(photo.venue_id) ?? 1) - 1));
        return next;
      });
      showSuccess('Photo deleted');
      void loadAllSubmissions(photo.venue_id);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeletePhotoBusy(false);
      setDeletePhotoTarget(null);
    }
  }

  /** "Primary" = the single photo the app shows (venues.approved_venue_photo_url). Locks the
   * new primary at carousel position #1 — everyone else keeps their relative order, shifted
   * down. This is the only action (besides the first-ever approval) that moves the primary. */
  async function makePrimaryPhoto(photo: ApprovedPhotoRow) {
    if (!photo.venue_id || !photo.public_url) return;
    setReorderBusy(true);
    try {
      const { error } = await supabase.rpc('admin_set_approved_venue_photo', {
        p_venue_id: photo.venue_id,
        p_url: photo.public_url,
      });
      if (error) throw error;

      const rest = selectedApprovedPhotos.filter((p) => p.id !== photo.id);
      const reordered = [photo, ...rest];
      const results = await Promise.all(
        reordered.map((p, idx) => supabase.from('venue_photos').update({ display_order: idx }).eq('id', p.id)),
      );
      const firstErr = results.find((r) => r.error)?.error;
      if (firstErr) throw firstErr;
      setSelectedApprovedPhotos(reordered.map((p, idx) => ({ ...p, display_order: idx })));

      setVenues((prev) =>
        prev.map((v) =>
          v.id === photo.venue_id ? { ...v, approved_venue_photo_url: photo.public_url } : v,
        ),
      );
      showSuccess('Set as primary photo — moved to position #1');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not set primary');
      if (selectedId) void loadApprovedPhotos(selectedId);
    } finally {
      setReorderBusy(false);
    }
  }

  /** Reorder the live-photo set by swapping with a neighbour and renumbering display_order. */
  async function movePhoto(photo: ApprovedPhotoRow, dir: 'up' | 'down') {
    const arr = [...selectedApprovedPhotos];
    const i = arr.findIndex((p) => p.id === photo.id);
    if (i < 0) return;
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSelectedApprovedPhotos(arr.map((p, idx) => ({ ...p, display_order: idx })));
    setReorderBusy(true);
    try {
      const results = await Promise.all(
        arr.map((p, idx) => supabase.from('venue_photos').update({ display_order: idx }).eq('id', p.id)),
      );
      const firstErr = results.find((r) => r.error)?.error;
      if (firstErr) throw firstErr;
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Reorder failed');
      if (selectedId) void loadApprovedPhotos(selectedId);
    } finally {
      setReorderBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-full bg-canvas admin-page">
        <PageHeader
          title="Venue review"
          subtitle="Venues with Shado ratings, filters, and photo review. Search opens any venue for editing."
        />
        <SkeletonRows rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full bg-canvas admin-page">
        <PageHeader
          title="Venue review"
          subtitle="Venues with Shado ratings, filters, and photo review. Search opens any venue for editing."
        />
        <ErrorBanner>{error}</ErrorBanner>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col bg-canvas admin-page lg:h-[calc(100vh-0px)]">
      <div className="shrink-0">
        <PageHeader
          title="Venue review"
          subtitle="Venues with Shado ratings, filters, and photo review. Search opens any venue for editing."
        />
      </div>

      <div className="mb-4 shrink-0 max-w-3xl w-full space-y-2">
        <label htmlFor="venue-admin-search" className="admin-table-head block">
          Search venues
        </label>
        <input
          id="venue-admin-search"
          type="search"
          value={venueSearchQuery}
          onChange={(e) => setVenueSearchQuery(e.target.value)}
          className="admin-input admin-input-lg"
        />
        {debouncedVenueSearch ? (
          <div className="rounded-lg border border-border bg-surface max-h-52 overflow-y-auto shadow-lg">
            {venueSearchLoading ? (
              <div className="px-4 py-3 text-sm text-muted">Searching…</div>
            ) : venueSearchResults.length === 0 ? (
              <div className="px-4 py-3 text-sm text-muted">No matches.</div>
            ) : (
              <ul className="divide-y divide-border/60">
                {venueSearchResults.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      onClick={() => void openVenueFromSearch(hit.id)}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-white/[0.04] transition-colors"
                    >
                      <span className="text-white font-medium">
                        {pickString(hit.venue_name_user_input) ?? 'Untitled venue'}
                      </span>
                      {pickString(hit.city_country) ? (
                        <span className="block text-xs text-mutedDim mt-0.5">{hit.city_country}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      <div
        ref={splitRef}
        className={`flex min-h-0 flex-1 flex-col gap-3 lg:flex-row ${resizing ? 'select-none' : ''}`}
      >
        <div
          style={{ '--venue-list-w': `${leftWidth}px` } as React.CSSProperties}
          className="flex max-h-[46vh] w-full min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-surface lg:max-h-none lg:w-[var(--venue-list-w)] lg:shrink-0"
        >
          <div className="border-b border-border/80 px-4 py-4 space-y-4 shrink-0">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-mutedDim">
                Rated venues
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <ExportButton
                  rows={filteredSortedVenues}
                  filename="venue-review"
                  label="Export CSV"
                  columns={[
                    { header: 'Name', get: (v: VenueRow) => venueName(v) },
                    { header: 'City', get: (v: VenueRow) => venueCity(v) },
                    { header: 'Ratings', get: (v: VenueRow) => ratingCountByVenue.get(v.id) ?? 0 },
                    { header: 'Pending', get: (v: VenueRow) => pendingByVenue.get(v.id)?.length ?? 0 },
                    { header: 'Shado score', get: (v: VenueRow) => venueMetaById.get(v.id)?.venue_shado_score ?? '' },
                    { header: 'FSQ popularity', get: (v: VenueRow) => venueMetaById.get(v.id)?.fsq_popularity ?? '' },
                    { header: 'Source', get: (v: VenueRow) => venueMetaById.get(v.id)?.platform_source ?? '' },
                    { header: 'ID', get: (v: VenueRow) => v.id },
                  ]}
                />
                <label className="flex items-center gap-2 text-xs text-muted">
                  <span className="text-mutedDim">Sort</span>
                  <select
                    value={listSortMode}
                    onChange={(e) => setListSortMode(e.target.value as ListSortMode)}
                    className="admin-input !w-auto !px-2 !text-xs"
                  >
                    <option value="ratings">Most ratings</option>
                    <option value="recent">Most recent activity</option>
                  </select>
                </label>
              </div>
            </div>
            <div
              className="flex flex-wrap gap-x-4 gap-y-3"
              role="group"
              aria-label="Filter venues"
            >
              {PHOTO_FILTER_OPTIONS.map(({ key, label, title }) => (
                <label
                  key={key}
                  title={title}
                  className="inline-flex cursor-pointer items-center gap-3 text-sm text-white/90 hover:text-white select-none"
                >
                  <input
                    type="checkbox"
                    checked={activePhotoFilters.includes(key)}
                    onChange={() => togglePhotoFilter(key)}
                    className="h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-mutedDim">
                Metadata
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-3" role="group" aria-label="Filter venues by metadata">
                {META_FILTER_OPTIONS.map(({ key, label, title }) => (
                  <label
                    key={key}
                    title={title}
                    className="inline-flex cursor-pointer items-center gap-3 text-sm text-white/90 hover:text-white select-none"
                  >
                    <input
                      type="checkbox"
                      checked={activeMetaFilters.includes(key)}
                      onChange={() => toggleMetaFilter(key)}
                      className="h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {venues.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted">No venues with Shado ratings yet.</p>
            ) : filteredSortedVenues.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted">No venues match these filters.</p>
            ) : (
              <ul>
                {filteredSortedVenues.map((v) => {
                  const id = v.id;
                  const pendingN = pendingByVenue.get(id)?.length ?? 0;
                  const ratings = ratingCountByVenue.get(id) ?? 0;
                  const thumb = livePhotoUrl(v);
                  const active = selectedId === id;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(id)}
                        className={`flex w-full items-center gap-3 border-b border-border/50 px-3 py-2.5 text-left transition-colors ${
                          active ? 'bg-accent/15' : 'hover:bg-white/[0.04]'
                        }`}
                      >
                        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md bg-border">
                          {thumb ? (
                            <img src={thumb} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full items-center justify-center text-[9px] text-mutedDim">—</div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`truncate text-sm ${active ? 'font-medium text-white' : 'text-white'}`}>
                            {venueName(v)}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-mutedDim">
                            <span className="tabular-nums">
                              {ratings.toLocaleString()} rating{ratings === 1 ? '' : 's'}
                            </span>
                            {pendingN > 0 ? (
                              <span className="rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-accent">
                                {pendingN} pending
                              </span>
                            ) : null}
                            {stillOnGoogle(v) ? (
                              <span
                                className="inline-block h-2 w-2 rounded-full bg-accent"
                                title="Using Google photo"
                              />
                            ) : null}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          onMouseDown={() => setResizing(true)}
          title="Drag to resize"
          className="group hidden w-2 shrink-0 cursor-col-resize items-center justify-center lg:flex"
        >
          <div
            className={`h-16 w-1 rounded-full ${resizing ? 'bg-accent' : 'bg-border group-hover:bg-accent/60'}`}
          />
        </div>

        <div className="min-w-0 flex-1 rounded-xl border border-border bg-surface lg:overflow-y-auto">
          {!selected ? (
            <div className="p-8 text-sm text-muted leading-relaxed">
              Select a venue from the table, or search above to open any venue.
            </div>
          ) : (
            <div className="space-y-10 p-4 lg:p-8">
              <div className="z-20 -mx-4 -mt-4 mb-1 border-b border-border bg-surface/95 px-4 py-4 backdrop-blur lg:sticky lg:top-0 lg:-mx-8 lg:-mt-8 lg:px-8">
                <h2 className="truncate font-display text-lg font-semibold tracking-tight text-white">
                  {venueEditDraft.venue_name_user_input.trim() ||
                    pickString(selected.venue_name_user_input) ||
                    'Untitled venue'}
                </h2>
                {pickString(selected.city_country) ? (
                  <p className="mt-0.5 truncate text-xs text-mutedDim">{venueCity(selected)}</p>
                ) : null}
              </div>
              <section className="max-w-2xl space-y-4 rounded-xl border border-border bg-canvas/40 p-5">
                <h3 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">Venue details</h3>
                <div className="space-y-1">
                  <label htmlFor={`vn-${selected.id}`} className="admin-table-head block">
                    Venue name
                  </label>
                  <input
                    id={`vn-${selected.id}`}
                    type="text"
                    value={venueEditDraft.venue_name_user_input}
                    onChange={(e) =>
                      setVenueEditDraft((d) => ({ ...d, venue_name_user_input: e.target.value }))
                    }
                    className="admin-input admin-input-lg"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor={`addr-${selected.id}`} className="admin-table-head block">
                    Address
                  </label>
                  <input
                    id={`addr-${selected.id}`}
                    type="text"
                    value={venueEditDraft.fsq_address}
                    onChange={(e) => setVenueEditDraft((d) => ({ ...d, fsq_address: e.target.value }))}
                    className="admin-input admin-input-lg"
                  />
                </div>
                {pickString(selected.city_country) ? (
                  <p className="text-xs text-mutedDim pt-1">
                    <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-mutedDim">
                      City / region (read-only)
                    </span>
                    <span className="block mt-1 text-sm text-muted">{venueCity(selected)}</span>
                  </p>
                ) : null}
                {venueFieldSupport.description ? (
                  <div className="space-y-1">
                    <label htmlFor={`desc-${selected.id}`} className="admin-table-head block">
                      Description
                    </label>
                    <textarea
                      id={`desc-${selected.id}`}
                      rows={4}
                      value={venueEditDraft.description}
                      onChange={(e) => setVenueEditDraft((d) => ({ ...d, description: e.target.value }))}
                      className="admin-input !h-auto !py-2.5 resize-y"
                    />
                  </div>
                ) : null}
                {venueFieldSupport.websiteUrl ? (
                  <div className="space-y-1">
                    <label htmlFor={`web-${selected.id}`} className="admin-table-head block">
                      Website
                    </label>
                    <input
                      id={`web-${selected.id}`}
                      type="text"
                      inputMode="url"
                      autoComplete="off"
                      value={venueEditDraft.website_url}
                      onChange={(e) => setVenueEditDraft((d) => ({ ...d, website_url: e.target.value }))}
                      className="admin-input admin-input-lg"
                    />
                  </div>
                ) : null}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    disabled={!venueEditDirty || venueSaveBusy}
                    onClick={() => void saveVenueDetails()}
                    className="admin-btn admin-btn-primary"
                  >
                    {venueSaveBusy ? 'Saving…' : 'Save venue details'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditVenueId(selected.id)}
                    className="rounded-lg border border-border bg-white/[0.04] px-4 py-2 text-sm font-semibold text-muted hover:text-white"
                  >
                    Edit coords / FSQ
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteOpen(true)}
                    className="ml-auto rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/20"
                  >
                    Delete venue
                  </button>
                </div>
              </section>

              {(() => {
                const rec = venueMetaById.get(selected.id) ?? null;
                const fmt = (v: unknown): string => {
                  if (v === null || v === undefined || v === '') return '—';
                  if (typeof v === 'boolean') return v ? 'true' : 'false';
                  return String(v);
                };
                const rows: { k: string; v: string }[] = [
                  { k: 'Shado score', v: fmt(rec?.venue_shado_score) },
                  { k: 'Unique users', v: fmt(rec?.venue_shado_unique_users) },
                  { k: 'Rating count', v: fmt(rec?.venue_shado_rating_count) },
                  { k: 'FSQ popularity', v: fmt(rec?.fsq_popularity) },
                  { k: 'Platform source', v: fmt(rec?.platform_source) },
                  { k: 'Latitude', v: fmt(rec?.latitude) },
                  { k: 'Longitude', v: fmt(rec?.longitude) },
                  { k: 'City ID', v: fmt(rec?.city_id) },
                  { k: 'FSQ place ID', v: fmt(rec?.fsq_place_id) },
                  { k: 'Google place ID', v: fmt(rec?.google_place_id) },
                  { k: 'Apple place ID', v: fmt(rec?.apple_place_id) },
                  { k: 'Reverse searched', v: fmt(rec?.reverse_searched) },
                  {
                    k: 'Created',
                    v: rec?.created_at ? `${timeAgo(rec.created_at)} · ${rec.created_at}` : '—',
                  },
                  { k: 'Venue ID', v: fmt(selected.id) },
                ];
                return (
                  <section className="max-w-2xl space-y-4 rounded-xl border border-border bg-canvas/40 p-5">
                    <h3 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">Record</h3>
                    {rec == null ? (
                      <p className="text-xs text-mutedDim">
                        Full record details unavailable — only the venue ID is shown below.
                      </p>
                    ) : null}
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                      {rows.map((r) => (
                        <div key={r.k} className="flex min-w-0 flex-col gap-0.5">
                          <dt className="admin-table-head">{r.k}</dt>
                          <dd className="break-all text-xs text-muted tabular-nums">{r.v}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                );
              })()}

              {stillOnGoogle(selected) ? (
                <div className="flex items-start gap-3 rounded-lg border border-accent/40 bg-accent/10 px-4 py-3.5 text-sm text-white/95 leading-relaxed">
                  <span className="font-bold text-accent shrink-0">!</span>
                  <span>Google photo is currently live for this venue. Approve a photo below to replace it.</span>
                </div>
              ) : null}

              <section>
                <h3 className="mb-1 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                  Live photos{selectedApprovedPhotos.length > 0 ? ` (${selectedApprovedPhotos.length})` : ''}
                </h3>
                <p className="mb-4 max-w-2xl text-xs leading-relaxed text-mutedDim">
                  The big number on each photo is its order in the app's photo carousel —{' '}
                  <span className="text-muted">1 shows first</span>. Use <span className="text-muted">↑ ↓</span> to
                  reorder. The one marked <span className="text-accent">★ Primary</span> is the single image shown on
                  the map and in lists — tap <span className="text-muted">Make primary</span> to change it.
                </p>
                {approvedPhotosLoading ? (
                  <div className="space-y-3" aria-hidden>
                    <Skeleton className="h-56 w-full rounded-xl" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : selectedApprovedPhotos.length === 0 ? (
                  <p className="text-sm text-muted">No approved photos for this venue.</p>
                ) : (
                  <ul className="space-y-5">
                    {selectedApprovedPhotos.map((ph, idx) => {
                      const isPrimary =
                        pickString(selected.approved_venue_photo_url) != null &&
                        pickString(selected.approved_venue_photo_url) === pickString(ph.public_url);
                      return (
                        <li
                          key={ph.id}
                          className={`overflow-hidden rounded-xl border bg-black ${
                            isPrimary ? 'border-accent ring-1 ring-accent/40' : 'border-border'
                          }`}
                        >
                          <div className="relative">
                            {ph.public_url ? (
                              <img
                                src={ph.public_url}
                                alt=""
                                className="max-h-[min(48dvh,560px)] w-full object-contain object-center"
                              />
                            ) : (
                              <div className="flex h-40 items-center justify-center text-sm text-muted">
                                No image URL
                              </div>
                            )}
                            <span
                              title={`Carousel position ${idx + 1}`}
                              className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-bold text-white backdrop-blur"
                            >
                              {idx + 1}
                            </span>
                            {isPrimary ? (
                              <span className="absolute right-3 top-3 rounded-full bg-accent px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg">
                                ★ Primary
                              </span>
                            ) : null}
                          </div>
                          <div className="border-t border-border bg-canvas px-4 py-3">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="admin-label-primary text-sm">{sourceLabel(ph.source)}</div>
                                <div className="mt-1 text-xs text-mutedDim">
                                  Position {idx + 1} of {selectedApprovedPhotos.length} · added{' '}
                                  {timeAgo(ph.created_at)} · by{' '}
                                  <span className="text-muted">{submitterLabel(ph.user_id)}</span>
                                </div>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <button
                                  type="button"
                                  disabled={reorderBusy || idx === 0}
                                  onClick={() => void movePhoto(ph, 'up')}
                                  title="Move earlier in carousel"
                                  className="rounded-md border border-border px-2.5 py-2 text-xs text-white hover:bg-white/[0.04] disabled:opacity-30"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  disabled={reorderBusy || idx === selectedApprovedPhotos.length - 1}
                                  onClick={() => void movePhoto(ph, 'down')}
                                  title="Move later in carousel"
                                  className="rounded-md border border-border px-2.5 py-2 text-xs text-white hover:bg-white/[0.04] disabled:opacity-30"
                                >
                                  ↓
                                </button>
                                {!isPrimary ? (
                                  <button
                                    type="button"
                                    disabled={reorderBusy}
                                    onClick={() => void makePrimaryPhoto(ph)}
                                    className="admin-btn admin-btn-primary admin-btn-sm"
                                  >
                                    Make primary
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  disabled={deletePhotoBusy}
                                  onClick={() => setDeletePhotoTarget(ph)}
                                  className="rounded-md bg-red-600/85 px-3 py-2 text-xs font-semibold text-white hover:bg-red-600 disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="mb-4 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">Pending</h3>
                {selectedPending.length === 0 ? (
                  <p className="text-sm text-muted">No pending photos for this venue.</p>
                ) : (
                  <ul className="space-y-10">
                    {selectedPending.map((ph) => (
                      <li
                        key={ph.id}
                        className="w-full max-w-[54rem] overflow-hidden rounded-xl border border-border bg-black"
                      >
                        {ph.public_url ? (
                          <img
                            src={ph.public_url}
                            alt=""
                            className="max-h-[min(61.5dvh,960px)] w-full object-contain object-center"
                          />
                        ) : (
                          <div className="flex min-h-[min(30dvh,270px)] items-center justify-center text-sm text-muted">
                            No image URL
                          </div>
                        )}
                        <div className="border-t border-border bg-canvas px-5 py-4">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <div className="admin-label-primary text-sm">{sourceLabel(ph.source)}</div>
                              <div className="text-xs text-mutedDim mt-1">
                                {timeAgo(ph.created_at)} · by{' '}
                                <span className="text-muted">{submitterLabel(ph.user_id)}</span>
                              </div>
                            </div>
                            <div className="flex shrink-0 gap-2">
                              <button
                                type="button"
                                disabled={actionId === ph.id || cropBusy}
                                onClick={() => setCropPhoto(ph)}
                                className="rounded-md bg-green-600/90 px-4 py-2 text-xs font-semibold text-white hover:bg-green-600 disabled:opacity-50"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                disabled={actionId === ph.id}
                                onClick={() => void rejectPhoto(ph)}
                                className="rounded-md bg-red-600/85 px-4 py-2 text-xs font-semibold text-white hover:bg-red-600 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="mb-1 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                  All submissions{allSubmissions.length > 0 ? ` (${allSubmissions.length})` : ''}
                </h3>
                <p className="mb-4 max-w-2xl text-xs leading-relaxed text-mutedDim">
                  Every photo ever submitted for this venue — live, pending, rejected, or later removed — newest
                  first. Nothing here is ever hard-deleted from the audit trail.
                </p>
                {allSubmissionsLoading ? (
                  <div className="space-y-2" aria-hidden>
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : allSubmissions.length === 0 ? (
                  <p className="text-sm text-muted">No submissions recorded for this venue.</p>
                ) : (
                  <ul className="divide-y divide-border/60 rounded-xl border border-border bg-canvas/40">
                    {allSubmissions.map((p) => {
                      const status = photoStatusLabel(p);
                      return (
                        <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-border">
                            {p.public_url ? (
                              <img src={p.public_url} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full items-center justify-center text-[9px] text-mutedDim">
                                —
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`admin-status admin-status-${status.tone}`}>{status.label}</span>
                              <span className="text-xs text-muted">{sourceLabel(p.source)}</span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-mutedDim">
                              by <span className="text-muted">{submitterLabel(p.user_id)}</span> · submitted{' '}
                              {timeAgo(p.created_at)}
                              {p.approved_at ? <> · approved {timeAgo(p.approved_at)}</> : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          )}
        </div>
      </div>

      {cropPhoto && cropImageUrl ? (
        <VenuePhotoCropModal
          imageUrl={cropImageUrl}
          title="Crop before approving"
          busy={cropBusy}
          onCancel={() => {
            if (!cropBusy) setCropPhoto(null);
          }}
          onConfirm={(pixelCrop) => void confirmCropAndApprove(pixelCrop)}
        />
      ) : cropPhoto ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 text-sm text-muted">
          Loading image…
        </div>
      ) : null}

      <VenueEditModal
        open={editVenueId != null && editVenueBase != null}
        base={editVenueBase}
        onClose={() => setEditVenueId(null)}
        onSaved={() => {
          setEditVenueId(null);
          void load();
        }}
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        danger
        title="Permanently delete this venue?"
        message={
          selected
            ? `Delete "${selected.venue_name_user_input?.trim() || 'this venue'}" and ALL of its ratings, photos, and wishlist entries? This removes it from the app everywhere — map, city pages, Near Me, search, and feed — and cannot be undone.`
            : ''
        }
        confirmLabel="Delete venue"
        cancelLabel="Cancel"
        busy={deleteBusy}
        onConfirm={() => void deleteVenue()}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      <ConfirmModal
        open={deletePhotoTarget != null}
        danger
        title="Delete this approved photo?"
        message="This removes the photo from the venue and deletes the stored image. If it's the live photo, the venue falls back to another approved photo (or its Google photo). This can't be undone."
        confirmLabel="Delete photo"
        cancelLabel="Cancel"
        busy={deletePhotoBusy}
        onConfirm={() => {
          if (deletePhotoTarget) void deleteApprovedPhoto(deletePhotoTarget);
        }}
        onCancel={() => setDeletePhotoTarget(null)}
      />

      {toastNode}
    </div>
  );
}
